import type { Text } from '@eformulieren/model'

/**
 * De twee talen van het product (B13, R-143), en het opmaken van een getal en
 * een datum daarin.
 *
 * **Zonder `Intl`, en dat is opzet.** De engine is isomorf: dezelfde suite draait
 * in Node en in de browser, en dat is zelf een testdoel van M1 (ontwerp 5.9). De
 * uitkomst van `Intl.NumberFormat` en `Intl.DateTimeFormat` hangt af van de
 * ICU-versie die de runtime meebrengt, dus twee runs zouden op de opmaak kunnen
 * verschillen terwijl de evaluatie identiek is. Dat zou het isomorfiebewijs
 * ondermijnen om een reden die niets met regels te maken heeft. De maanden en de
 * scheidingstekens staan daarom hier, in twaalf plus twee regels.
 */

export const LANGUAGES = ['nl', 'en'] as const
export type Language = (typeof LANGUAGES)[number]

/** Bouwt het taalpaar uit de twee losse teksten. */
export function pair(nl: string, en: string): Text {
  return { nl, en }
}

/** Bouwt het taalpaar door dezelfde functie per taal aan te roepen. */
export function perLanguage(create: (language: Language) => string): Text {
  return { nl: create('nl'), en: create('en') }
}

const THOUSANDS_SEPARATOR = { nl: '.', en: ',' } as const
const DECIMAL = { nl: ',', en: '.' } as const

/**
 * Een getal in de opmaak van de taal: `2.000` en `0,5` in het Nederlands,
 * `2,000` en `0.5` in het Engels.
 *
 * Een getal dat JavaScript in wetenschappelijke notatie schrijft (vanaf 1e21)
 * gaat ongewijzigd door. Zo'n grens komt in een formulier niet voor, en hem
 * uitschrijven zou een melding opleveren die niemand leest.
 */
export function formatNumber(value: number, language: Language): string {
  if (!Number.isFinite(value)) return String(value)
  const raw = Math.abs(value).toString()
  if (raw.includes('e')) return String(value)
  const [whole, decimals] = raw.split('.')
  const grouped = (whole ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, THOUSANDS_SEPARATOR[language])
  const character = value < 0 ? '-' : ''
  return decimals === undefined ? `${character}${grouped}` : `${character}${grouped}${DECIMAL[language]}${decimals}`
}

const MONTHS = {
  nl: [
    'januari',
    'februari',
    'maart',
    'april',
    'mei',
    'juni',
    'juli',
    'augustus',
    'september',
    'oktober',
    'november',
    'december',
  ],
  en: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
} as const

/**
 * Een datum als `1 januari 2026` of `1 January 2026`, uit de vorm jjjj-mm-dd die
 * het schema afdwingt.
 *
 * Beide talen zetten de dag vooraan. Dat is de Nederlandse vorm en de
 * internationale Engelse vorm; de Amerikaanse omkering zou de twee talen zonder
 * winst uit elkaar laten lopen.
 *
 * Komt er toch iets anders binnen dan jjjj-mm-dd, dan gaat de invoer ongewijzigd
 * door. Een melding is geen plek om een fout op te werpen.
 */
export function date(value: string, language: Language): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (parts === null) return value
  const [, year, month, day] = parts
  const name = MONTHS[language][Number(month) - 1]
  if (name === undefined) return value
  return `${Number(day)} ${name} ${year}`
}

/** Een opsomming met het juiste laatste voegwoord: `a, b of c` en `a, b or c`. */
export function formatList(parts: readonly string[], language: Language): string {
  if (parts.length === 0) return ''
  if (parts.length === 1) return parts[0] ?? ''
  const last = parts[parts.length - 1] ?? ''
  const conjunction = language === 'nl' ? 'of' : 'or'
  return `${parts.slice(0, -1).join(', ')} ${conjunction} ${last}`
}
